import { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import styled from '@emotion/styled';
import { useNavigate, useLocation } from 'react-router-dom';
import { CircularProgress, Dialog, DialogContent, IconButton } from '@mui/material';
import { useConsumerT } from '../translations/landingConsumer';
import { useLandingPageContext } from '../pages/Landing-consumer/LandingPageContext';
import { useParcelLoader } from '../hooks/useParcelLoader';
import ErrorPageV3 from '../pages/global/ErrorPageV3';
import heroBackground from '../../assets/logos/background-mob.svg';
import AlertSnackbar from '../components/AlertSnackbar';

export default function LandingScreenContainer() {
  const t = useConsumerT();
  const navigate = useNavigate();
  const location = useLocation();
  const { fetchAlert, alertMessage, dispatch: landingPageDispatch } = useLandingPageContext();
  const { brand, parcel, loading, notFound } = useParcelLoader();

  const parcelNotFound = location.state?.parcelNotFound === true;
  const [lotteryOpen, setLotteryOpen] = useState(false);
  const confettiFiredRef = useRef(false);

  useEffect(() => {
    if (!loading && !notFound && !confettiFiredRef.current) {
      confettiFiredRef.current = true;
      if (!sessionStorage.getItem('lottery-popup-shown')) {
        sessionStorage.setItem('lottery-popup-shown', 'true');
        confetti({
          particleCount: 120,
          spread: 75,
          origin: { y: 0.35 },
          colors: ['#D9A641', '#F4E5C3', '#FFD700', '#FFF9E6', '#C79434', '#56A6C3', '#FFFFFF'],
          startVelocity: 35,
          gravity: 0.85,
          ticks: 220,
        });
        setLotteryOpen(true);
      }
    }
  }, [loading, notFound]);

  function handleAlertClose() {
    landingPageDispatch({ type: 'PAGE_LANDING_STATES_UPDATE', payload: { fetchAlert: false, alertMessage: '' } });
  }

  function handleNotFoundClose() {
    navigate('/', { replace: true, state: null });
  }

  if (loading) {
    return (
      <Centered>
        <CircularProgress size={32} sx={{ color: '#56A6C3' }} />
      </Centered>
    );
  }

  if (notFound) {
    return <ErrorPageV3 errorCode="404" message={t('landing.error-invalid-token')} />;
  }

  const displayBrand = brand || {};
  const bgColor = displayBrand?.brandGradient || `linear-gradient(150deg, #2EA9C7 0%, #1B2E28 100%)`;
  const textColor = displayBrand?.brandTextColor || '#FFFFFF';
  const brandLogoUrl = displayBrand?.brandLogoUrl;
  const isValidUrl = brandLogoUrl && (brandLogoUrl.startsWith('http') || brandLogoUrl.startsWith('/'));
  const heroBg = displayBrand?.heroImageUrl || heroBackground;

  return (
    <Page>
      <AlertSnackbar open={fetchAlert} message={alertMessage} onClose={handleAlertClose} />

      <Dialog
        open={parcelNotFound}
        onClose={handleNotFoundClose}
        PaperProps={{ sx: { borderRadius: 4, m: 2.5, maxWidth: 320 } }}
      >
        <DialogContent sx={{ position: 'relative', pt: 4, pb: 4, px: 3, textAlign: 'center' }}>
          <IconButton
            onClick={handleNotFoundClose}
            size="small"
            sx={{ position: 'absolute', top: 10, right: 10, color: 'text.disabled' }}
          >
            ✕
          </IconButton>
          <NotFoundEmoji>📦</NotFoundEmoji>
          <NotFoundTitle>Emballage introuvable</NotFoundTitle>
          <NotFoundDialogText>{t('landing.error-invalid-token')}</NotFoundDialogText>
        </DialogContent>
      </Dialog>

      <Dialog
        open={lotteryOpen}
        onClose={() => setLotteryOpen(false)}
        PaperProps={{ sx: { borderRadius: 4, m: 2.5, maxWidth: 340 } }}
      >
        <DialogContent sx={{ position: 'relative', pt: 4, pb: 3, px: 3, textAlign: 'center' }}>
          <IconButton
            onClick={() => setLotteryOpen(false)}
            size="small"
            sx={{ position: 'absolute', top: 10, right: 10, color: 'text.disabled' }}
          >
            ✕
          </IconButton>
          <LotteryEmoji>🎟️</LotteryEmoji>
          <LotteryDialogTitle>{t('landing.lottery-popup-title')}</LotteryDialogTitle>
          <LotteryDialogBody>{t('landing.lottery-popup-body')}</LotteryDialogBody>
          <LotteryDialogNote>{t('landing.lottery-popup-note')}</LotteryDialogNote>
          <LotteryOkBtn type="button" onClick={() => setLotteryOpen(false)}>
            {t('landing.lottery-popup-ok')}
          </LotteryOkBtn>
        </DialogContent>
      </Dialog>

      <Hero $bgColor={bgColor} $heroBg={heroBg} $textColor={textColor}>
        <HeroInner>
          <MainHeading>{t('landing.thanks-heading')}</MainHeading>
          <SubHeading>{t('landing.thanks-subheading')}</SubHeading>

          {isValidUrl && (
            <LogoContainer>
              <LogoImage src={brandLogoUrl} alt={displayBrand.brandName || 'Brand logo'} />
            </LogoContainer>
          )}

          <FrostedCard>
            <FrostedText $textColor={textColor}>{t('landing.commitment-intro')}</FrostedText>
            <FrostedTextBold $textColor={textColor}>{t('landing.commitment-cta')}</FrostedTextBold>
          </FrostedCard>
        </HeroInner>
      </Hero>

      <SheetZone>
        <LotteryTeaser type="button" onClick={() => setLotteryOpen(true)}>
          <LotteryTeaserEmoji>🎟️</LotteryTeaserEmoji>
          <LotteryTeaserText>
            <LotteryTeaserTitle>{t('landing.lottery-popup-title')}</LotteryTeaserTitle>
            <LotteryTeaserSub>{t('landing.lottery-teaser-sub')}</LotteryTeaserSub>
          </LotteryTeaserText>
          <LotteryTeaserArrow>›</LotteryTeaserArrow>
        </LotteryTeaser>

        <PrimaryBtn type="button" onClick={() => navigate(parcel?.id ? '/deposit' : '/scan?actionType=returnPackage')}>
          {t('landing.cta-return-package')}
        </PrimaryBtn>

        <Separator>
          <span>{t('landing.cta-separator')}</span>
        </Separator>

        <DarkBtn type="button" onClick={() => navigate(parcel?.id ? '/sendback' : '/scan?actionType=returnOrder')}>
          {t('landing.cta-return-order')}
        </DarkBtn>

        <Footer>{t('landing.operated-by')}</Footer>
      </SheetZone>
    </Page>
  );
}

const Centered = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.palette.background.default};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 32px;
  font-family: ${p => p.theme.typography.fontFamily};
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${p => p.theme.palette.background.paper};
  font-family: ${p => p.theme.typography.fontFamily};
  display: flex;
  flex-direction: column;
`;

const Hero = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 56px 20px 48px;
  color: ${p => p.$textColor || '#FFFFFF'};
  background-image: url(${p => p.$heroBg}), ${p => p.$bgColor};
  background-size: cover, cover;
  background-position: center, center;
  background-repeat: no-repeat, no-repeat;
  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0,0,0,0.12);
    pointer-events: none;
  }
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 480px;
  text-align: center;
`;

const MainHeading = styled.h1`
  font-size: 52px;
  font-weight: 700;
  letter-spacing: -1px;
  margin: 0 0 4px;
  line-height: 1;
`;

const SubHeading = styled.p`
  font-size: 20px;
  font-weight: 600;
  margin: 0 0 20px;
  letter-spacing: -0.3px;
`;

const LogoContainer = styled.div`
  margin: 24px 0;
  display: flex;
  align-items: center;
  justify-content: center;

  & > div {
    max-width: 180px;
    max-height: 100px;
  }

  svg {
    max-width: 180px;
    max-height: 100px;
    width: auto;
    height: auto;
    filter: drop-shadow(0 2px 8px rgba(0,0,0,0.15));
  }
`;

const LogoImage = styled.img`
  max-width: 180px;
  max-height: 100px;
  width: auto;
  height: auto;
  object-fit: contain;
  border-radius: 8px;
  filter: drop-shadow(0 2px 8px rgba(0,0,0,0.15));
`;

const FrostedCard = styled.div`
  background: rgba(255,255,255,0.13);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 1px solid rgba(255,255,255,0.2);
  border-radius: 16px;
  padding: 20px 22px;
  text-align: justify;
`;

const FrostedText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  margin: 0 0 10px;
  color: ${p => p.$textColor ? `${p.$textColor}eb` : 'rgba(255,255,255,0.92)'};
`;

const FrostedTextBold = styled(FrostedText)`
  font-weight: 700;
  color: ${p => p.$textColor || '#FFFFFF'};
`;

const SheetZone = styled.div`
  background: ${p => p.theme.palette.background.paper};
  border-radius: 20px 20px 0 0;
  margin-top: -20px;
  padding: 24px 20px 32px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  position: relative;
  z-index: 2;
`;

const GOLD_PRIMARY = '#D9A641';
const GOLD_DARK = '#8B6914';
const CREAM = '#FFF9E6';

const LotteryTeaser = styled.button`
  width: 100%;
  background: linear-gradient(135deg, ${CREAM} 0%, #FDEFC9 50%, #F4E5C3 100%);
  border: 2px solid ${GOLD_PRIMARY};
  border-left: 5px solid ${GOLD_PRIMARY};
  border-radius: 16px;
  padding: 14px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  text-align: left;
  box-shadow: 0 4px 24px rgba(217, 166, 65, 0.2), 0 0 40px rgba(217, 166, 65, 0.12);
  margin-bottom: 4px;
  font-family: ${p => p.theme.typography.fontFamily};
  transition: transform 0.12s ease, box-shadow 0.12s ease;
  &:active {
    transform: scale(0.98);
    box-shadow: 0 2px 12px rgba(217, 166, 65, 0.15);
  }
`;

const LotteryTeaserEmoji = styled.span`
  font-size: 28px;
  flex-shrink: 0;
  filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.15));
`;

const LotteryTeaserText = styled.div`
  flex: 1;
  min-width: 0;
`;

const LotteryTeaserTitle = styled.div`
  font-size: 13px;
  font-weight: 800;
  color: ${GOLD_DARK};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  line-height: 1.3;
`;

const LotteryTeaserSub = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: rgba(139, 105, 20, 0.75);
  margin-top: 2px;
  line-height: 1.4;
`;

const LotteryTeaserArrow = styled.span`
  font-size: 22px;
  color: ${GOLD_PRIMARY};
  font-weight: 700;
  flex-shrink: 0;
  line-height: 1;
`;

const LotteryEmoji = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
  line-height: 1;
`;

const LotteryDialogTitle = styled.h2`
  font-size: 17px;
  font-weight: 800;
  color: ${GOLD_DARK};
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin: 0 0 12px;
`;

const LotteryDialogBody = styled.p`
  font-size: 14px;
  line-height: 1.65;
  color: ${p => p.theme.palette.text.primary};
  margin: 0 0 10px;
`;

const LotteryDialogNote = styled.p`
  font-size: 13px;
  font-weight: 600;
  color: ${GOLD_DARK};
  margin: 0 0 20px;
`;

const LotteryOkBtn = styled.button`
  width: 100%;
  padding: 13px 24px;
  border-radius: 12px;
  background: ${GOLD_PRIMARY};
  border: none;
  color: #FFFFFF;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  font-family: ${p => p.theme.typography.fontFamily};
  &:active {
    opacity: 0.85;
  }
`;

const btnBase = `
  width: 100%;
  padding: 15px 20px;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  letter-spacing: -0.2px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
`;

const NotFoundEmoji = styled.div`
  font-size: 48px;
  margin-bottom: 12px;
  line-height: 1;
`;

const NotFoundTitle = styled.h2`
  font-size: 17px;
  font-weight: 700;
  color: ${p => p.theme.palette.text.primary};
  margin: 0 0 8px;
`;

const NotFoundDialogText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${p => p.theme.palette.text.secondary};
  margin: 0;
`;

const PrimaryBtn = styled.button`
  ${btnBase}
  background: ${p => p.theme.palette.accent.main};
  border: none;
  color: #FFFFFF;
  box-shadow: ${p => p.theme.shadows[2]};
  font-family: ${p => p.theme.typography.fontFamily};
`;

const DarkBtn = styled.button`
  ${btnBase}
  background: ${p => p.theme.palette.text.primary};
  border: none;
  color: #FFFFFF;
  font-family: ${p => p.theme.typography.fontFamily};
`;

const Separator = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 4px 0;
  &::before, &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: ${p => p.theme.palette.border.main};
  }
  span {
    font-size: 11px;
    font-weight: 600;
    color: ${p => p.theme.palette.text.disabled};
    text-transform: uppercase;
    letter-spacing: 1.5px;
  }
`;

const Footer = styled.div`
  text-align: center;
  margin-top: auto;
  padding-top: 32px;
  font-size: 10px;
  font-weight: 600;
  color: ${p => p.theme.palette.text.disabled};
  text-transform: uppercase;
  letter-spacing: 1.5px;
`;
